<template>
  <div class="daily-revenue">
    <div class="drc-head">
      <h5 class="drc-title">Billed per day — {{ monthLabel }}</h5>
      <p class="drc-sub">
        What was billed on each day of the month — ride charges, extra users, white label
        fees, adjustments and manual lines. This is daily usage, not the card charge taken
        at the end of the month.
      </p>
    </div>

    <div class="drc-tiles">
      <div class="drc-tile">
        <span class="drc-tile-label">Month total</span>
        <span class="drc-tile-value">{{ money(total) }} <small>{{ currency }}</small></span>
      </div>
      <div class="drc-tile">
        <span class="drc-tile-label">Busiest day</span>
        <span class="drc-tile-value">
          <template v-if="busiestDay">{{ money(busiestDay.amount) }} <small>{{ currency }}</small></template>
          <template v-else>—</template>
        </span>
        <span class="drc-tile-foot">{{ busiestDay ? dayLabel(busiestDay) : 'no activity' }}</span>
      </div>
      <div class="drc-tile">
        <span class="drc-tile-label">Average per day</span>
        <span class="drc-tile-value">{{ money(average) }} <small>{{ currency }}</small></span>
        <span class="drc-tile-foot">over {{ elapsedDays }} day{{ elapsedDays === 1 ? '' : 's' }}</span>
      </div>
      <div class="drc-tile">
        <span class="drc-tile-label">Days with income</span>
        <span class="drc-tile-value">{{ activeDayCount }} <small>/ {{ days.length }}</small></span>
      </div>
    </div>

    <div v-if="total === 0 && activeDayCount === 0" class="drc-empty">
      No billing activity recorded in {{ monthLabel }}.
    </div>

    <div v-else class="drc-chart">
      <div class="drc-yaxis">
        <span
          v-for="tick in ticks"
          :key="tick.value"
          class="drc-ytick"
          :style="{ bottom: tick.percent + '%' }"
        >{{ compact(tick.value) }}</span>
      </div>

      <div ref="plot" class="drc-plot">
        <div
          v-for="tick in ticks"
          :key="`grid-${tick.value}`"
          class="drc-gridline"
          :style="{ bottom: tick.percent + '%' }"
        />

        <div
          v-if="average > 0"
          class="drc-avgline"
          :style="{ bottom: percentOf(average) + '%' }"
        >
          <span class="drc-avglabel">avg</span>
        </div>

        <div class="drc-cols">
          <div
            v-for="(day, index) in days"
            :key="day.date"
            class="drc-col"
            @mouseenter="hovered = index"
            @mouseleave="hovered = null"
            @click="hovered = hovered === index ? null : index"
          >
            <div
              class="drc-bar"
              :class="{ 'drc-bar-zero': day.amount <= 0, 'drc-bar-active': hovered === index }"
              :style="{ height: barHeight(day.amount) }"
              :title="`${dayLabel(day)}: ${money(day.amount)} ${currency}`"
            />
          </div>
        </div>

        <div
          v-if="hoveredDay"
          ref="tooltip"
          class="drc-tooltip"
          :style="{ left: tooltipX + 'px', bottom: tooltipBottom + '%' }"
        >
          <strong>{{ money(hoveredDay.amount) }} {{ currency }}</strong>
          <span>{{ dayLabel(hoveredDay) }}</span>
        </div>
      </div>

      <div class="drc-xaxis">
        <div v-for="day in days" :key="`x-${day.date}`" class="drc-xcell">
          <span v-if="showDayTick(day.day)">{{ day.day }}</span>
        </div>
      </div>
    </div>

    <div class="drc-foot">
      <button type="button" class="btn btn-link btn-sm drc-toggle" @click="showTable = !showTable">
        {{ showTable ? 'Hide numbers' : 'Show numbers' }}
      </button>
    </div>

    <div v-if="showTable" class="table-responsive drc-table">
      <table class="table table-sm mb-0">
        <thead class="thead-light">
          <tr>
            <th>Day</th>
            <th class="text-right">Billed</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="day in activeDays" :key="`row-${day.date}`">
            <td>{{ dayLabel(day) }}</td>
            <td class="text-right">{{ money(day.amount) }} {{ currency }}</td>
          </tr>
          <tr v-if="!activeDays.length">
            <td colspan="2" class="text-muted">No billing activity in this month.</td>
          </tr>
          <tr v-if="activeDays.length" class="font-weight-bold">
            <td>Total</td>
            <td class="text-right">{{ money(total) }} {{ currency }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

import {
  buildDailySeries,
  daysInMonth,
  elapsedDaysInMonth,
  niceAxisScale,
} from '@/utils/dailyRevenue';

export default {
  name: 'DailyRevenueChart',
  props: {
    // Rows of the admin monthly summary, already filtered to the selected month.
    rows: { type: Array, required: true },
    year: { type: Number, required: true },
    month: { type: Number, required: true },
  },
  data() {
    return {
      hovered: null,
      showTable: false,
      tooltipX: 0,
      lastTooltipWidth: 110,
    };
  },
  computed: {
    monthLabel() {
      return `${MONTH_NAMES[this.month - 1]} ${this.year}`;
    },
    currency() {
      const withCurrency = this.rows.find(row => row.currency);
      return withCurrency ? withCurrency.currency : 'ILS';
    },
    daysInMonth() {
      return daysInMonth(this.year, this.month);
    },
    // One entry per calendar day, so the month always keeps the same shape.
    days() {
      return buildDailySeries(this.rows, this.year, this.month);
    },
    activeDays() {
      return this.days.filter(day => day.amount !== 0);
    },
    activeDayCount() {
      return this.activeDays.length;
    },
    total() {
      return this.days.reduce((sum, day) => sum + day.amount, 0);
    },
    maxAmount() {
      return this.days.reduce((max, day) => Math.max(max, day.amount), 0);
    },
    // For the month in progress, only count days that have actually happened.
    elapsedDays() {
      return elapsedDaysInMonth(this.year, this.month);
    },
    average() {
      return this.elapsedDays ? this.total / this.elapsedDays : 0;
    },
    busiestDay() {
      if (this.maxAmount <= 0) return null;
      return this.days.find(day => day.amount === this.maxAmount) || null;
    },
    scale() {
      return niceAxisScale(Math.max(this.maxAmount, this.average));
    },
    axisMax() {
      return this.scale.max;
    },
    ticks() {
      return this.scale.ticks.map(value => ({
        value,
        percent: this.percentOf(value),
      }));
    },
    hoveredDay() {
      return this.hovered === null ? null : this.days[this.hovered];
    },
    // Sits just above the hovered bar, clamped so the whole card never has to
    // make room for it — the tooltip is ~46px in a 200px plot, hence the 70% cap.
    tooltipBottom() {
      if (!this.hoveredDay) return 0;
      return Math.min(this.percentOf(this.hoveredDay.amount) + 4, 70);
    },
  },
  watch: {
    rows() {
      this.hovered = null;
    },
    hovered() {
      this.positionTooltip();
    },
  },
  methods: {
    /**
     * Centre the tooltip on the hovered column, then clamp it to the plot so it
     * cannot spill out at the first or last day — which is what a percentage
     * transform could not guarantee on a narrow phone.
     */
    positionTooltip() {
      if (this.hovered === null) return;
      const place = (tooltipWidth) => {
        const plot = this.$refs.plot;
        if (!plot) return;
        const plotWidth = plot.clientWidth;
        const centre = ((this.hovered + 0.5) / this.days.length) * plotWidth;
        this.tooltipX = Math.max(0, Math.min(centre - tooltipWidth / 2, plotWidth - tooltipWidth));
      };
      // Place immediately with the last measured width so nothing flashes,
      // then correct once this tooltip's real width is known.
      place(this.lastTooltipWidth);
      this.$nextTick(() => {
        const tooltip = this.$refs.tooltip;
        if (!tooltip) return;
        this.lastTooltipWidth = tooltip.offsetWidth;
        place(tooltip.offsetWidth);
      });
    },
    percentOf(value) {
      if (!this.axisMax) return 0;
      return Math.max(0, Math.min(100, (value / this.axisMax) * 100));
    },
    barHeight(amount) {
      if (amount <= 0) return '0%';
      return `${this.percentOf(amount)}%`;
    },
    showDayTick(day) {
      return day === 1 || day % 5 === 0;
    },
    dayLabel(day) {
      return `${day.day} ${MONTH_NAMES[this.month - 1]}`;
    },
    money(value) {
      return Number(value || 0).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    compact(value) {
      if (value >= 1000) {
        const thousands = value / 1000;
        return `${thousands % 1 === 0 ? thousands : thousands.toFixed(1)}K`;
      }
      return Number(value).toLocaleString('en-US', { maximumFractionDigits: 0 });
    },
  },
};
</script>

<style scoped>
.daily-revenue {
  /* The app flips <html> to dir="rtl" in Hebrew (main.js), which mirrors the flex
     row of day columns while the tooltip and the y axis are placed with physical
     left/right — the tooltip then appeared on the opposite side of the chart.
     A day axis runs 1 → 31 left to right in either language, so pin it to LTR. */
  direction: ltr;
  --drc-series: #2a78d6;
  --drc-surface: #ffffff;
  --drc-grid: #e9ecef;
  --drc-ink: #212529;
  --drc-ink-muted: #6c757d;
  padding: 4px 0 0;
}

.drc-head {
  margin-bottom: 14px;
}

.drc-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--drc-ink);
  margin: 0 0 4px;
}

.drc-sub {
  font-size: 12px;
  color: var(--drc-ink-muted);
  margin: 0;
  max-width: 96ch;
}

/* ── stat tiles ───────────────────────────────────────────────────────────── */
.drc-tiles {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 18px;
}

.drc-tile {
  display: flex;
  flex-direction: column;
  flex: 1 1 130px;
  padding: 10px 12px;
  border: 1px solid var(--drc-grid);
  border-radius: 6px;
  background: var(--drc-surface);
}

.drc-tile-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--drc-ink-muted);
}

.drc-tile-value {
  font-size: 19px;
  font-weight: 600;
  color: var(--drc-ink);
  line-height: 1.3;
}

.drc-tile-value small {
  font-size: 12px;
  font-weight: 400;
  color: var(--drc-ink-muted);
}

.drc-tile-foot {
  font-size: 11px;
  color: var(--drc-ink-muted);
}

/* ── plot ─────────────────────────────────────────────────────────────────── */
.drc-chart {
  position: relative;
  padding-left: 42px;
}

.drc-yaxis {
  position: absolute;
  left: 0;
  top: 0;
  width: 38px;
  height: 200px;
}

.drc-ytick {
  position: absolute;
  right: 0;
  transform: translateY(50%);
  font-size: 11px;
  color: var(--drc-ink-muted);
  font-variant-numeric: tabular-nums;
}

.drc-plot {
  position: relative;
  height: 200px;
}

.drc-gridline {
  position: absolute;
  left: 0;
  right: 0;
  height: 1px;
  background: var(--drc-grid);
}

.drc-avgline {
  position: absolute;
  left: 0;
  right: 0;
  height: 0;
  border-top: 1px dashed #adb5bd;
  z-index: 2;
}

.drc-avglabel {
  position: absolute;
  right: 0;
  top: -14px;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--drc-ink-muted);
  background: var(--drc-surface);
  padding: 0 3px;
}

.drc-cols {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  top: 0;
  display: flex;
  gap: 2px;
  z-index: 1;
}

/* The whole column is the hover target, not just the bar. */
.drc-col {
  flex: 1 1 0;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  cursor: default;
}

.drc-bar {
  width: 100%;
  max-width: 24px;
  min-height: 3px;
  background: var(--drc-series);
  border-radius: 4px 4px 0 0;
  transition: opacity 0.1s ease-in-out;
}

.drc-bar-zero {
  min-height: 2px;
  background: var(--drc-grid);
  border-radius: 2px 2px 0 0;
}

.drc-bar-active {
  opacity: 0.78;
}

.drc-tooltip {
  position: absolute;
  display: flex;
  flex-direction: column;
  white-space: nowrap;
  padding: 6px 9px;
  border-radius: 6px;
  background: var(--drc-ink);
  color: #fff;
  z-index: 4;
  pointer-events: none;
}

.drc-tooltip strong {
  font-size: 13px;
  font-weight: 600;
}

.drc-tooltip span {
  font-size: 11px;
  opacity: 0.8;
}

.drc-xaxis {
  display: flex;
  gap: 2px;
  margin-top: 6px;
}

.drc-xcell {
  flex: 1 1 0;
  text-align: center;
  font-size: 10px;
  color: var(--drc-ink-muted);
  font-variant-numeric: tabular-nums;
}

.drc-empty {
  padding: 28px 0;
  text-align: center;
  font-size: 13px;
  color: var(--drc-ink-muted);
}

.drc-foot {
  margin-top: 4px;
}

.drc-toggle {
  padding: 0;
  font-size: 12px;
}

.drc-table {
  margin-top: 6px;
}

.drc-table td,
.drc-table th {
  font-size: 13px;
}

.drc-table td.text-right,
.drc-table th.text-right {
  font-variant-numeric: tabular-nums;
}

@media (max-width: 575px) {
  /* two tiles per row, so the chart itself is not pushed off the first screen */
  .drc-tile { flex: 1 1 calc(50% - 4px); }
  .drc-tile-value { font-size: 17px; }
  .drc-chart { padding-left: 34px; }
  .drc-yaxis { width: 30px; height: 160px; }
  .drc-plot { height: 160px; }
}
</style>
